import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  OutlinedInput,
  Radio,
  RadioGroup,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { SxProps } from "@mui/system";
import { MuiTelInput } from "mui-tel-input";
import { FC } from "react";

import CheckboxGroup from "./inputs/CheckboxGroup";
import CheckboxInput from "./inputs/CheckboxInput";
import FileField from "./inputs/FileField";
import IbanField from "./inputs/IbanField";
import LocationInput from "./inputs/LocationInput";
import StationInput from "./inputs/StationInput";
import TextArea from "./TextArea";

/* eslint-disable @typescript-eslint/no-shadow */
export enum InputType {
  Checkbox = "checkbox",
  CheckboxGroup = "checkboxGroup",
  DateTime = "dateTime",
  Email = "email",
  Euro = "euro",
  Iban = "iban",
  File = "file",
  Location = "location",
  Number = "number",
  RadioGroup = "radioGroup",
  Station = "station",
  Tel = "tel",
  Text = "text",
  TextArea = "textarea",
  Url = "url",
}
/* eslint-enable @typescript-eslint/no-shadow */

export type InputValue = string | string[] | number | boolean;

export type ChangeHandler = (e: {
  target: {
    name: string;
    value: InputValue;
    stationId?: string;
  };
}) => void;

export interface RendererProps {
  autoFocus?: boolean;
  contentType?: string;
  sx?: SxProps;
  label: string;
  name: string;
  onChange?: ChangeHandler;
  options?: Array<{ label: string; value: string }>;
  placeholder?: string;
  required?: boolean;
  defaultValue?: string | string[] | number;
  value?: InputValue;
}

const RENDERERS: Record<InputType, FC<RendererProps>> = {
  [InputType.DateTime]: ({ label, ...props }) => (
    <TextField {...props} fullWidth type="datetime-local" />
  ),
  [InputType.Email]: ({ label, ...props }) => (
    <TextField {...props} fullWidth type="email" />
  ),
  [InputType.Euro]: ({ label, ...props }) => (
    <OutlinedInput
      {...props}
      inputProps={{ step: ".01" }}
      startAdornment={<InputAdornment position="start">€</InputAdornment>}
      type="number"
      onBlur={(e) => {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        e.target.value = parseFloat(e.target.value).toFixed(2);
      }}
    />
  ),
  [InputType.Text]: ({ label, ...props }) => (
    <TextField {...props} fullWidth type="text" />
  ),
  [InputType.Number]: ({ label, ...props }) => (
    <TextField {...props} fullWidth type="number" />
  ),
  [InputType.Iban]: (props) => <IbanField {...props} />,
  [InputType.File]: (props) => <FileField {...props} />,
  [InputType.Tel]: (props) => (
    <MuiTelInput
      {...props}
      fullWidth
      defaultCountry="NL"
      preferredCountries={["NL"]}
      value={props.value as string}
      onChange={(value) =>
        props.onChange?.({ target: { name: props.name, value: value ?? "" } })
      }
    />
  ),
  [InputType.Location]: (props) => <LocationInput {...props} />,
  [InputType.TextArea]: (props) => <TextArea {...props} />,
  [InputType.Station]: (props) => <StationInput {...props} />,
  [InputType.RadioGroup]: ({ options, ...props }) => (
    <RadioGroup {...props}>
      {options?.map(({ label, value }) => (
        <FormControlLabel
          control={<Radio />}
          key={value}
          label={label}
          value={value}
        />
      ))}
    </RadioGroup>
  ),
  [InputType.CheckboxGroup]: (props) => <CheckboxGroup {...props} />,
  [InputType.Checkbox]: (props) => <CheckboxInput {...props} />,
  [InputType.Url]: (props) => <TextField {...props} fullWidth type="url" />,
};

const Input = ({
  autoFocus,
  contentType,
  defaultValue,
  error,
  helperText,
  label,
  name,
  onChange,
  options,
  placeholder,
  required,
  sx,
  type,
  value,
}: RendererProps & {
  error?: string;
  helperText?: string;
  flexDirection?: string;
  type: InputType;
}) => {
  const Renderer = RENDERERS[type];

  return (
    <FormControl error={!!error} required={required}>
      <FormLabel>{label}</FormLabel>
      <Renderer
        autoFocus={autoFocus}
        contentType={contentType}
        defaultValue={defaultValue}
        label={label}
        name={name}
        options={options}
        placeholder={placeholder}
        required={required}
        sx={sx}
        value={value}
        onChange={onChange}
      />
      {(error || helperText) && (
        <FormHelperText>{error ?? helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default Input;
